import { useState } from "react";
import { Check, Plus, Settings2, Tag } from "lucide-react";
import type { LeadLabel, PIPELINE_STAGE_COLORS } from "@school-sales-ai/shared";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { LabelChip } from "@/components/LabelChip";
import { PIPELINE_COLOR_OPTIONS } from "@/lib/stage-colors";
import { useLabelsContext } from "@/components/labels/labels-context";
import { useCrmStore } from "@/stores/crm.store";

type PaletteColor = (typeof PIPELINE_STAGE_COLORS)[number];

type Props = {
  leadId: string;
  labels: LeadLabel[];
  // Detail contexts show a × on each chip to remove it; the compact card doesn't.
  removable?: boolean;
};

function swatchClass(color: string | null): string {
  return (
    PIPELINE_COLOR_OPTIONS.find((c) => c.name === color)?.swatch ?? "bg-ink-300"
  );
}

export function CardLabels({ leadId, labels, removable = false }: Props): React.JSX.Element {
  // One query + one set of mutations for the whole board, not one per card.
  const { allLabels, assign, unassign, create, isCreating } = useLabelsContext();
  const openManageLabels = useCrmStore((s) => s.openManageLabels);

  const [createOpen, setCreateOpen] = useState(false);
  const [newName, setNewName] = useState("");
  const [newColor, setNewColor] = useState<PaletteColor>("blue");

  const assignedIds = new Set(labels.map((l) => l.id));

  function toggle(label: LeadLabel): void {
    if (assignedIds.has(label.id)) {
      unassign(leadId, label.id);
    } else {
      assign(leadId, label.id);
    }
  }

  function handleCreate(): void {
    const name = newName.trim();
    if (!name) return;
    create({ name, color: newColor }, (label) => {
      assign(leadId, label.id);
      setNewName("");
      setNewColor("blue");
      setCreateOpen(false);
    });
  }

  return (
    <div className="flex flex-wrap items-center gap-1">
      {labels.map((label) => (
        <LabelChip
          key={label.id}
          label={label}
          onRemove={
            removable
              ? () => unassign(leadId, label.id)
              : undefined
          }
        />
      ))}

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            aria-label="Agregar etiqueta"
            onPointerDown={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}
            className="flex h-5 items-center gap-0.5 rounded-full border border-dashed border-ink-300 px-1.5 text-[11px] text-ink-500 hover:border-ink-400 hover:text-ink-700"
          >
            <Plus className="h-3 w-3" />
            {labels.length === 0 && <span>Etiqueta</span>}
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="start"
          className="max-h-72 w-56 overflow-y-auto"
          onClick={(e) => e.stopPropagation()}
        >
          <DropdownMenuLabel className="flex items-center gap-1.5 text-[11px] font-medium text-ink-500">
            <Tag className="h-3 w-3" />
            Etiquetas
          </DropdownMenuLabel>

          {allLabels.length === 0 && (
            <p className="px-2 py-1.5 text-[11px] text-ink-400">
              Aún no hay etiquetas.
            </p>
          )}

          {allLabels.map((label) => (
            <DropdownMenuItem
              key={label.id}
              // Keep the menu open so several labels can be toggled at once.
              onSelect={(e) => {
                e.preventDefault();
                toggle(label);
              }}
              className="gap-2"
            >
              <span className={`h-3 w-3 rounded-full ${swatchClass(label.color)}`} />
              <span className="flex-1 truncate">{label.name}</span>
              {assignedIds.has(label.id) && <Check className="h-3.5 w-3.5" />}
            </DropdownMenuItem>
          ))}

          <DropdownMenuSeparator />
          <DropdownMenuItem
            onSelect={(e) => {
              e.preventDefault();
              setCreateOpen(true);
            }}
            className="gap-2"
          >
            <Plus className="h-3.5 w-3.5" />
            Nueva etiqueta
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={() => openManageLabels()} className="gap-2">
            <Settings2 className="h-3.5 w-3.5" />
            Administrar etiquetas
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Mounted on demand: a Dialog root per card is ~500 of them on a full board. */}
      {createOpen && (
      <Dialog open onOpenChange={setCreateOpen}>
        <DialogContent className="sm:max-w-sm" onClick={(e) => e.stopPropagation()}>
          <DialogHeader>
            <DialogTitle>Nueva etiqueta</DialogTitle>
          </DialogHeader>

          <div className="flex flex-col gap-3">
            <input
              type="text"
              autoFocus
              value={newName}
              maxLength={40}
              onChange={(e) => setNewName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleCreate();
              }}
              placeholder="Nombre de la etiqueta"
              className="rounded-lg border border-ink-200 px-3 py-2 text-sm text-ink-900 focus:border-moss focus:outline-none"
            />
            <div className="flex items-center gap-1">
              {PIPELINE_COLOR_OPTIONS.map((c) => (
                <button
                  key={c.name}
                  type="button"
                  aria-label={`Color ${c.name}`}
                  onClick={() => setNewColor(c.name)}
                  className={`h-6 w-6 rounded-full ${c.swatch} ${
                    newColor === c.name ? "ring-2 ring-ink-900 ring-offset-1" : ""
                  }`}
                />
              ))}
            </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setCreateOpen(false)}
              disabled={isCreating}
            >
              Cancelar
            </Button>
            <Button
              type="button"
              onClick={handleCreate}
              disabled={!newName.trim() || isCreating}
            >
              {isCreating ? "Creando…" : "Crear"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      )}
    </div>
  );
}
